import { twMerge } from "tailwind-merge";
import { Badge } from "../badge/Badge";
import { TooltipContent } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { Tooltip } from "@sellar-io/components";
import { Fragment, ReactNode } from "react";

type DietaryDisplayMode = "text" | "badge";

export type DietaryType = "VG" | "LF" | "GF";

type DietaryDisplayListParameters = {
  dietaryRequirements: Record<DietaryType, boolean>;
  mode: DietaryDisplayMode;
};

export const DietaryDisplayList = ({
  dietaryRequirements,
  mode,
}: DietaryDisplayListParameters) => {
  const validDietaryTypes = (
    Object.keys(dietaryRequirements) as DietaryType[]
  ).filter((dietaryType) => Boolean(dietaryRequirements[dietaryType]));

  if (validDietaryTypes.length === 0) {
    return null;
  }

  if (mode === "text") {
    return (
      <div className="flex flex-wrap gap-px">
        {validDietaryTypes.map((dietaryType, idx) => {
          const isLastElement = validDietaryTypes.length === idx + 1;
          return (
            <Fragment key={dietaryType}>
              <DietaryTypeText dietaryType={dietaryType} />
              {!isLastElement && <span className="text-sm">, </span>}
            </Fragment>
          );
        })}
      </div>
    );
  }

  if (mode === "badge") {
    return (
      <div className="flex flex-wrap gap-1">
        {validDietaryTypes.map((dietaryType) => (
          <DietaryTypeBadge key={dietaryType} dietaryType={dietaryType} />
        ))}
      </div>
    );
  }

  return null;
};

const DietaryTypeText = ({ dietaryType }: { dietaryType: DietaryType }) => {
  const { textColor } = getDietaryTypeTextColour(dietaryType);
  return (
    <DietaryTooltipWrapper key={dietaryType} dietaryType={dietaryType}>
      <span
        className={twMerge("cursor-pointer text-sm font-medium", textColor)}
      >
        {dietaryType}
      </span>
    </DietaryTooltipWrapper>
  );
};

const DietaryTypeBadge = ({ dietaryType }: { dietaryType: DietaryType }) => {
  const { textColor, backgroundColor } = getDietaryTypeBadgeColour(dietaryType);
  return (
    <DietaryTooltipWrapper dietaryType={dietaryType}>
      <Badge
        className={twMerge(
          textColor,
          backgroundColor,
          "cursor-pointer text-sm font-medium",
        )}
      >
        {dietaryType}
      </Badge>
    </DietaryTooltipWrapper>
  );
};

const DietaryTooltipWrapper = ({
  dietaryType,
  children,
}: {
  dietaryType: DietaryType;
  children: ReactNode;
}) => {
  return (
    <Tooltip placement="top-start">
      <TooltipTrigger className="inline-flex">{children}</TooltipTrigger>
      <TooltipContent
        className={
          "rounded-md border border-zinc-300 bg-white p-2 text-start text-sm font-semibold text-zinc-800 shadow-lg"
        }
      >
        {getDietaryTypeTooltipMessage(dietaryType)}
      </TooltipContent>
    </Tooltip>
  );
};

const getDietaryTypeTextColour = (dietary: DietaryType) => {
  switch (dietary) {
    case "VG":
      return {
        textColor: "text-emerald-800",
      };
    case "GF":
      return {
        textColor: "text-yellow-800",
      };
    case "LF":
      return {
        textColor: "text-blue-800",
      };
    default:
      return {
        textColor: "text-zinc-600",
      };
  }
};

const getDietaryTypeBadgeColour = (dietary: DietaryType) => {
  switch (dietary) {
    case "VG":
      return {
        textColor: "text-emerald-800",
        backgroundColor: "bg-emerald-100",
      };
    case "GF":
      return {
        textColor: "text-yellow-800",
        backgroundColor: "bg-yellow-100",
      };
    case "LF":
      return {
        textColor: "text-blue-800",
        backgroundColor: "bg-blue-100",
      };
    default:
      return {
        textColor: "text-zinc-800",
        backgroundColor: "transparent",
      };
  }
};

const getDietaryTypeTooltipMessage = (dietaryType: DietaryType) => {
  switch (dietaryType) {
    case "VG":
      return "Vegan";
    case "LF":
      return "Lactose Free";
    case "GF":
      return "Gluten Free";
    default:
      return "";
  }
};
